import { useState } from "react";
import { CalendarPlus } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "./ui/dialog";
import { Button } from "./ui/button";

const FREQ = [
  { value: "weekly", label: "Weekly", count: 13, cadence: "weekly" },
  { value: "fortnightly", label: "Fortnightly", count: 6, cadence: "weekly" },
  { value: "monthly", label: "Monthly", count: 3, cadence: "monthly" },
];

function nextMondayLocal() {
  const d = new Date();
  d.setDate(d.getDate() + ((8 - d.getDay()) % 7 || 7));
  d.setHours(9, 0, 0, 0);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Creates a whole quarter of scheduled sessions in one call. */
export default function CreateSeriesDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("Weekly board meeting");
  const [freq, setFreq] = useState("weekly");
  const [pair, setPair] = useState(true);
  const [start, setStart] = useState(nextMondayLocal);
  const [submitting, setSubmitting] = useState(false);

  const spec = FREQ.find((f) => f.value === freq);
  const sessionCount = spec.count * (pair ? 2 : 1);

  const submit = async () => {
    if (!title.trim()) {
      toast.error("Give the series a title");
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.post("/meetings/series", {
        title: title.trim(),
        frequency: freq,
        cadence: spec.cadence,
        start: new Date(start).toISOString(),
        count: spec.count,
        pair_with_kickoff: pair,
      });
      toast.success(`${r.data.count} sessions on the docket.`);
      setOpen(false);
      onCreated && onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create the series");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center justify-center gap-1.5 border border-[#2a2a2a] hover:border-[#c89f45] text-[#eaeaea] hover:text-[#c89f45] rounded-md h-11 sm:h-10 px-3 sm:px-4 text-sm transition-colors whitespace-nowrap"
          data-testid="create-series-trigger"
        >
          <CalendarPlus size={14} /> Create a quarter
        </button>
      </DialogTrigger>
      <DialogContent className="bg-[#0a0a0a] border border-[#2a2a2a] text-[#fafafa]" data-testid="create-series-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Book a quarter of sessions</DialogTitle>
          <DialogDescription className="text-[#8a8a8a]">
            One click puts the whole run on the docket. Each one can be renamed, moved or added to your calendar later.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <label className="label-eyebrow block mb-1.5">Series title</label>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full bg-[#121212] border border-[#2a2a2a] rounded-md px-3 py-2.5 text-base sm:text-sm text-[#eaeaea] min-h-[44px]"
              data-testid="series-title-input"
            />
          </div>
          <div>
            <label className="label-eyebrow block mb-1.5">Frequency</label>
            <select
              value={freq}
              onChange={(e) => setFreq(e.target.value)}
              className="w-full bg-[#121212] border border-[#2a2a2a] rounded-md px-3 py-2.5 text-base sm:text-sm text-[#eaeaea] min-h-[44px]"
              data-testid="series-frequency-select"
            >
              {FREQ.map((f) => (
                <option key={f.value} value={f.value}>{f.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label-eyebrow block mb-1.5">Structure</label>
            <div className="space-y-2">
              {[
                { value: true, label: "Kickoff + review", hint: "Open each period naming your goals, close it scoring them" },
                { value: false, label: "Reviews only", hint: "One retrospective session each period" },
              ].map((opt) => (
                <button
                  key={String(opt.value)}
                  type="button"
                  onClick={() => setPair(opt.value)}
                  className={`w-full text-left rounded-md border px-3 py-2.5 transition-colors ${
                    pair === opt.value
                      ? "border-[#c89f45] bg-[#c89f45]/10"
                      : "border-[#2a2a2a] hover:border-[#3a3a3a]"
                  }`}
                  data-testid={`series-structure-${opt.value ? "pair" : "review"}`}
                >
                  <p className={`text-sm ${pair === opt.value ? "text-[#c89f45]" : "text-[#eaeaea]"}`}>{opt.label}</p>
                  <p className="text-xs text-[#8a8a8a] mt-0.5">{opt.hint}</p>
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="label-eyebrow block mb-1.5">First session</label>
            <input
              type="datetime-local"
              value={start}
              onChange={(e) => setStart(e.target.value)}
              className="w-full bg-[#121212] border border-[#2a2a2a] rounded-md px-3 py-2.5 text-base sm:text-sm text-[#eaeaea] min-h-[44px]"
              data-testid="series-start-input"
            />
          </div>
          <p className="text-xs font-mono text-[#c89f45]" data-testid="series-summary">
            CREATES {sessionCount} SESSIONS · {spec.label.toUpperCase()}{pair ? " KICKOFF + REVIEW" : ""} · ONE QUARTER
          </p>
        </div>

        <DialogFooter>
          <Button
            onClick={submit}
            disabled={submitting}
            className="bg-[#c89f45] hover:bg-[#b48c34] text-[#0a0a0a] font-medium h-11 sm:h-10 w-full sm:w-auto"
            data-testid="series-submit"
          >
            {submitting ? "Booking…" : `Book ${sessionCount} sessions`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
